// v19.29 — Admin beat upload + management page.
// Route: /admin/beats
// Requires the admin JWT (crow_admin_2026 password screen — same login
// flow as /admin dashboard). Every upload is stored in Emergent Object
// Storage under `crow-daw/store-beats/{id}/…`.
import GlobalNav from "../components/GlobalNav";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UploadSimple, Trash, Eye, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const priceFmt = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;
const API_URL = process.env.REACT_APP_BACKEND_URL;

function useAdminToken() {
  const [token, setToken] = useState(() => localStorage.getItem("crow_admin_token") || "");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const login = async () => {
    setErr("");
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error("Invalid password");
      const data = await res.json();
      localStorage.setItem("crow_admin_token", data.token);
      setToken(data.token);
    } catch (e) { setErr(e.message); }
  };
  return { token, setToken, password, setPassword, err, login };
}

export default function AdminBeatsPage() {
  const nav = useNavigate();
  const auth = useAdminToken();
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(false);

  // v19.32 P0 — Depend on `auth.token` (a stable string) instead of
  // the whole `auth` object. `useAdminToken` returns a new object on
  // every render, so the previous `useCallback([auth])` produced a
  // new `load` reference on every render → `useEffect([load])` fired
  // → `setLoading` re-rendered → new `auth` → repeat forever. The
  // resulting render storm hammered /api/store/admin/beats hundreds
  // of times per second, tripped edge throttling on the deployed
  // preview, and surfaced as "Load failed" + a never-resolving
  // "Loading…" list. This also blocked the upload's success path
  // from settling in the UI because the row list was being wiped
  // between the POST and the follow-up GET. Fix: memoise on the
  // token string only, and inline the token-clearing logic so the
  // callback doesn't close over `auth.setToken` (which is also
  // fresh every render).
  const token = auth.token;
  const clearToken = () => {
    localStorage.removeItem("crow_admin_token");
    auth.setToken("");
  };
  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/store/admin/beats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401 || res.status === 403) {
        clearToken();
        return;
      }
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const data = await res.json();
      setBeats(data.beats || []);
    } catch (e) { toast.error(e.message || "Load failed"); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!auth.token) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-3">
          <h1 className="font-display font-bold text-2xl">Admin — Beat Store</h1>
          <input type="password" value={auth.password} onChange={(e) => auth.setPassword(e.target.value)} placeholder="Admin password" className="w-full px-3 py-2 rounded-md bg-[#0a0a0a] border border-[#262626]" onKeyDown={(e) => { if (e.key === "Enter") auth.login(); }} data-testid="admin-beats-password" />
          {auth.err ? <div className="text-red-400 text-xs">{auth.err}</div> : null}
          <button onClick={auth.login} className="w-full py-2 rounded-md bg-emerald-500 text-black font-bold" data-testid="admin-beats-login">Sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => nav("/admin")} className="text-white/50 hover:text-white text-sm flex items-center gap-1"><ArrowLeft size={16} weight="bold" /> Dashboard</button>
          <GlobalNav />
          <div className="flex-1" />
          <button onClick={() => { localStorage.removeItem("crow_admin_token"); auth.setToken(""); }} className="text-xs text-white/50 hover:text-white">Sign out</button>
        </div>
        <h1 className="font-display font-bold text-4xl mb-6">Beat Store — Admin</h1>

        <ReingestCard token={auth.token} />

        <FulfillmentErrorsCard token={auth.token} />

        <UploadForm token={auth.token} onCreated={load} />

        <h2 className="font-display font-bold text-2xl mt-10 mb-3">All beats</h2>
        {loading ? <div className="text-white/40 text-sm">Loading…</div> : (
          <div className="space-y-3">
            {beats.length === 0 ? <div className="text-white/40 text-sm">No beats uploaded yet.</div> : beats.map((b) => (
              <BeatRow key={b.id} b={b} token={auth.token} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReingestCard({ token }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (busy) return;
    if (!window.confirm("Re-ingest every admin-owned saved project into the AI generator corpus?\n\nThis is safe to run repeatedly — it upserts snapshots and auto-tags legacy projects (e.g. \"Bizmo Afrobeat\" → Afro).")) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/curated-projects/reingest`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Re-ingest failed (${res.status})`);
      const data = await res.json();
      setResult(data);
      toast.success(`Re-ingest complete — ${data.rebuilt} project(s) rebuilt.`);
    } catch (e) {
      toast.error(e.message || "Re-ingest failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mb-6 rounded-lg border border-[#262626] bg-[#0a0a0a] p-4">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-bold text-lg">AI Generator Corpus</div>
          <div className="text-xs text-white/50 mt-1">
            Rebuild every admin-owned project snapshot through the latest extraction
            pipeline. Auto-tags legacy projects by name (e.g. <span className="text-white/70">Bizmo Afrobeat</span> → Afro)
            and prunes stale snapshots whose source project has been deleted.
          </div>
        </div>
        <button
          onClick={run}
          disabled={busy}
          data-testid="admin-reingest-projects-btn"
          className="px-4 py-2 rounded-md bg-emerald-500 text-black font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "Re-ingesting…" : "Re-ingest Projects"}
        </button>
      </div>
      {result ? (
        <div className="mt-3 text-xs text-white/70 space-y-1" data-testid="admin-reingest-result">
          <div>
            <span className="text-emerald-400 font-bold">{result.rebuilt}</span> rebuilt
            {" · "}<span className="text-white/50">{result.skipped_no_genre}</span> skipped (no genre)
            {" · "}<span className="text-white/50">{result.skipped_missing_source}</span> stale removed
            {" · "}<span className="text-white/50">{result.skipped_non_admin}</span> non-admin
          </div>
          {result.genre_counts ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {Object.entries(result.genre_counts).map(([g, n]) => (
                <span key={g} className="px-2 py-0.5 rounded bg-[#1a1a1a] border border-[#262626] text-[10px] uppercase tracking-wide">
                  {g} · {n}
                </span>
              ))}
            </div>
          ) : null}
          {result.errors && result.errors.length > 0 ? (
            <div className="text-red-400 pt-1">{result.errors.length} error(s) — first: {result.errors[0].error}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FulfillmentErrorsCard({ token }) {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/store/admin/fulfillment-errors`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const data = await res.json();
      setErrors(data.errors || []);
    } catch (e) {
      toast.error(e.message || "Could not load fulfillment errors");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  const retry = async (row) => {
    if (!row.stripe_session_id) { toast.error("This error has no Stripe session — retry unavailable."); return; }
    if (!window.confirm(`Retry fulfillment for ${row.buyer_email || "buyer"} on session ${row.stripe_session_id}?`)) return;
    setRetrying((s) => ({ ...s, [row.id]: true }));
    try {
      const res = await fetch(`${API_URL}/api/store/admin/fulfillment/retry`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ stripe_session_id: row.stripe_session_id }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`;
        throw new Error(detail);
      }
      toast.success("Retry sent — refreshing…");
      await fetch(`${API_URL}/api/store/admin/fulfillment-errors/${row.id}/resolve`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) {
      toast.error(e.message || "Retry failed");
    } finally {
      setRetrying((s) => ({ ...s, [row.id]: false }));
    }
  };
  const resolve = async (row) => {
    try {
      await fetch(`${API_URL}/api/store/admin/fulfillment-errors/${row.id}/resolve`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Marked resolved");
      await load();
    } catch (e) { toast.error(e.message || "Could not resolve"); }
  };
  const unresolved = errors.filter((e) => !e.resolved);
  return (
    <div className="mb-6 rounded-lg border border-[#262626] bg-[#0a0a0a] p-4" data-testid="admin-fulfillment-errors-card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-bold text-lg flex items-center gap-2">
            <Warning size={18} weight="bold" className={unresolved.length ? "text-red-400" : "text-emerald-400"} />
            Fulfillment Errors
          </div>
          <div className="text-xs text-white/50 mt-1">
            Every failed fulfillment step (PDF gen, storage upload, Resend email) lands here so you see exactly what broke. Retry re-runs the pipeline with the buyer email stored on the original checkout intent.
          </div>
        </div>
        <div className="text-xs">
          <span className={`px-2 py-0.5 rounded ${unresolved.length ? "bg-red-500/10 border border-red-400/40 text-red-300" : "bg-emerald-500/10 border border-emerald-400/40 text-emerald-300"} font-bold`} data-testid="fulfillment-errors-count">
            {unresolved.length} unresolved
          </span>
          <button onClick={load} className="ml-2 text-white/50 hover:text-white underline" data-testid="fulfillment-errors-refresh">refresh</button>
        </div>
      </div>
      {loading ? <div className="mt-3 text-xs text-white/40">Loading…</div> : null}
      {!loading && errors.length === 0 ? <div className="mt-3 text-xs text-emerald-300/70">No fulfillment errors — clean pipeline.</div> : null}
      {!loading && errors.length > 0 ? (
        <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1">
          {errors.map((row) => (
            <div key={row.id} className={`rounded-lg border px-3 py-2 text-xs ${row.resolved ? "border-white/10 bg-black/30 opacity-60" : "border-red-400/40 bg-red-500/5"}`}>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <span className="font-bold text-red-300 mr-2">{row.step}</span>
                  <span className="text-white/80">{row.buyer_email || "(no email)"}</span>
                  <span className="text-white/40 ml-2">{(row.created_at || "").slice(0, 19).replace("T", " ")}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {row.stripe_session_id ? (
                    <button
                      onClick={() => retry(row)}
                      disabled={row.resolved || retrying[row.id]}
                      className="px-2 py-0.5 rounded bg-emerald-500 text-black font-bold disabled:opacity-40"
                      data-testid={`retry-${row.id}`}
                    >{retrying[row.id] ? "Retrying…" : "Retry"}</button>
                  ) : null}
                  {!row.resolved ? (
                    <button onClick={() => resolve(row)} className="px-2 py-0.5 rounded bg-white/10 hover:bg-white/20" data-testid={`resolve-${row.id}`}>
                      Resolve
                    </button>
                  ) : (
                    <span className="text-emerald-300">Resolved</span>
                  )}
                </div>
              </div>
              <div className="mt-1 text-white/60 break-all">{row.detail}</div>
              {row.stripe_session_id ? <div className="text-[10px] text-white/40 mt-0.5">session: {row.stripe_session_id}</div> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function UploadForm({ token, onCreated }) {
  const [name, setName] = useState("");
  const [artist, setArtist] = useState("");
  const [bpm, setBpm] = useState(140);
  const [key, setKey] = useState("");
  const [tags, setTags] = useState("");
  const prices = { mp3: 30, wav: 100, stems: 100, exclusive: 250 };
  const [taggedMp3, setTaggedMp3] = useState(null);
  const [untaggedMp3, setUntaggedMp3] = useState(null);
  const [untaggedWav, setUntaggedWav] = useState(null);
  const [stemsZip, setStemsZip] = useState(null);
  const [nameStatus, setNameStatus] = useState(null); // { available:bool, suggestions:[] }
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({}); // { untagged_mp3: 0-100, ... }
  const [suggested, setSuggested] = useState([]);

  const checkName = async () => {
    if (!name.trim() || !artist.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/store/admin/beats/check-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ artist_tag: artist, beat_name: name }),
      });
      const data = await res.json();
      setNameStatus(data);
    } catch { /* noop */ }
  };

  const suggestNames = async () => {
    try {
      const res = await fetch(`${API_URL}/api/store/beats/suggest-names`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ genre: "trap", mood: "street" }),
      });
      const data = await res.json();
      setSuggested(data.names || []);
    } catch { toast.error("Could not fetch suggestions"); }
  };

  const submit = async () => {
    if (!name.trim() || !artist.trim() || !taggedMp3) { toast.error("Beat name, artist tag, and tagged MP3 are required"); return; }
    setBusy(true);
    setProgress({});
    // v19.34 — SPLIT UPLOAD. Real beats regularly hit 200-400 MB
    // (stems zip alone is often 150 MB+). A single multipart POST
    // with all four files fails on production because the K8s
    // ingress rejects bodies over ~100 MB with a bare 413 that the
    // browser's fetch turns into an opaque "Upload failed". Splitting
    // means each request carries ONE file, staying comfortably under
    // every ingress limit AND giving the operator per-file progress
    // + per-file error surfacing (so future failures name the
    // specific file that broke instead of a bare toast).
    const uploadWithProgress = (url, formData, onProgress) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
        } else {
          let detail = xhr.responseText;
          try { detail = JSON.parse(xhr.responseText).detail || detail; } catch { /* keep raw */ }
          reject(new Error(`${xhr.status}: ${detail?.slice(0, 250) || xhr.statusText}`));
        }
      };
      xhr.onerror = () => reject(new Error("Network error — check your connection or the file size (K8s ingress may reject >100 MB)"));
      xhr.ontimeout = () => reject(new Error("Upload timed out — try again on a faster connection"));
      xhr.send(formData);
    });
    const putWithProgress = (url, formData, onProgress) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
        } else {
          let detail = xhr.responseText;
          try { detail = JSON.parse(xhr.responseText).detail || detail; } catch { /* keep raw */ }
          reject(new Error(`${xhr.status}: ${detail?.slice(0, 250) || xhr.statusText}`));
        }
      };
      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.ontimeout = () => reject(new Error("File upload timed out"));
      xhr.send(formData);
    });

    try {
      // Step 1: Create beat with metadata + tagged MP3 (small).
      const fd = new FormData();
      fd.append("beat_name", name);
      fd.append("artist_tag", artist);
      fd.append("bpm", String(bpm));
      fd.append("musical_key", key);
      fd.append("tags", tags);
      fd.append("price_mp3", String(Math.round(prices.mp3 * 100)));
      fd.append("price_wav", String(Math.round(prices.wav * 100)));
      fd.append("price_stems", String(Math.round(prices.stems * 100)));
      fd.append("price_exclusive", String(Math.round(prices.exclusive * 100)));
      fd.append("tagged_mp3", taggedMp3);
      const data = await uploadWithProgress(`${API_URL}/api/store/admin/beats`, fd, (p) => setProgress((prev) => ({ ...prev, tagged_mp3: p })));
      const beatId = data?.beat?.id;
      if (!beatId) throw new Error("Server did not return the new beat id");

      // Step 2: Attach each remaining large file individually.
      const followUps = [
        { file: untaggedMp3, kind: "untagged_mp3" },
        { file: untaggedWav, kind: "untagged_wav" },
        { file: stemsZip, kind: "stems_zip" },
      ].filter((x) => !!x.file);
      for (const { file, kind } of followUps) {
        const one = new FormData();
        one.append("kind", kind);
        one.append("file", file);
        setProgress((prev) => ({ ...prev, [kind]: 0 }));
        await putWithProgress(`${API_URL}/api/store/admin/beats/${beatId}/file`, one, (p) => setProgress((prev) => ({ ...prev, [kind]: p })));
      }

      toast.success(`Uploaded "${data.beat.beat_name}" under ${data.beat.artist_tag}`);
      setName(""); setTaggedMp3(null); setUntaggedMp3(null); setUntaggedWav(null); setStemsZip(null); setNameStatus(null); setProgress({});
      onCreated();
    } catch (e) {
      // Surface the FULL error so a future failure never becomes a
      // bare "Upload failed" toast again. Includes HTTP status + the
      // backend's `detail` string (which now names the specific file
      // + size + underlying error via v19.34's _save_upload_to_storage
      // hardening).
      toast.error(e.message || "Upload failed", { duration: 12000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-[#0a0a0a] border border-[#1f1f1f] p-5 sm:p-6 space-y-4" data-testid="admin-beats-upload-form">
      <div className="font-bold text-lg flex items-center gap-2"><UploadSimple size={18} weight="bold" /> Upload a new beat</div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] text-white/50 mb-1">Artist tag</label>
          <input value={artist} onChange={(e) => setArtist(e.target.value)} onBlur={checkName} placeholder="Topoppgen Type Beat" className="w-full px-3 py-2 rounded-md bg-black border border-[#262626] text-sm" data-testid="admin-beats-artist-tag" />
        </div>
        <div>
          <label className="block text-[11px] text-white/50 mb-1 flex items-center gap-2">Beat name
            <button type="button" onClick={suggestNames} className="ml-auto text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 hover:border-emerald-400/40 hover:text-emerald-300" data-testid="admin-beats-dice">🎲 Suggest names</button>
          </label>
          <input value={name} onChange={(e) => { setName(e.target.value.toUpperCase()); setNameStatus(null); }} onBlur={checkName} placeholder="SLEEPY" className="w-full px-3 py-2 rounded-md bg-black border border-[#262626] text-sm font-mono uppercase" data-testid="admin-beats-name" />
          {nameStatus && !nameStatus.available ? (
            <div className="mt-2 text-[11px] text-red-400 flex items-start gap-1">
              <Warning size={12} weight="bold" className="mt-0.5" />
              Name taken under this artist tag. Try: {(nameStatus.suggestions || []).map((s) => (
                <button key={s} type="button" onClick={() => { setName(s); setNameStatus({ available: true }); }} className="ml-1 underline hover:text-emerald-300">{s}</button>
              ))}
            </div>
          ) : nameStatus?.available ? (
            <div className="mt-1 text-[11px] text-emerald-300">Name available.</div>
          ) : null}
          {suggested.length > 0 ? (
            <div className="mt-2 text-[11px] text-white/50">Ideas: {suggested.map((s) => (
              <button key={s} type="button" onClick={() => { setName(s); setSuggested([]); setNameStatus(null); }} className="ml-1 mb-1 inline-block px-1.5 py-0.5 rounded bg-white/5 border border-white/10 hover:border-emerald-400/40 hover:text-emerald-300 font-mono">{s}</button>
            ))}</div>
          ) : null}
        </div>
        <div>
          <label className="block text-[11px] text-white/50 mb-1">BPM</label>
          <input type="number" value={bpm} onChange={(e) => setBpm(parseInt(e.target.value || "0", 10))} className="w-full px-3 py-2 rounded-md bg-black border border-[#262626] text-sm" data-testid="admin-beats-bpm" />
        </div>
        <div>
          <label className="block text-[11px] text-white/50 mb-1">Key</label>
          <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="F# Minor" className="w-full px-3 py-2 rounded-md bg-black border border-[#262626] text-sm" data-testid="admin-beats-key" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[11px] text-white/50 mb-1">Tags (comma-separated)</label>
          <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="trap, dark, vibe" className="w-full px-3 py-2 rounded-md bg-black border border-[#262626] text-sm" data-testid="admin-beats-tags" />
        </div>
      </div>

      <div className="text-xs text-white/50 rounded-md border border-white/10 px-3 py-2" data-testid="admin-beats-fixed-prices">
        Fixed store pricing: MP3 Lease $30 · WAV + Stems Lease $100 · Exclusive $250
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FileInput label="Tagged MP3 preview (required)" testid="admin-beats-file-tagged" onChange={setTaggedMp3} accept="audio/mpeg,audio/mp3,.mp3" progress={progress.tagged_mp3} />
        <FileInput label="Untagged MP3" testid="admin-beats-file-mp3" onChange={setUntaggedMp3} accept="audio/mpeg,audio/mp3,.mp3" progress={progress.untagged_mp3} />
        <FileInput label="Untagged WAV" testid="admin-beats-file-wav" onChange={setUntaggedWav} accept="audio/wav,.wav" progress={progress.untagged_wav} />
        <FileInput label="Stems zip" testid="admin-beats-file-stems" onChange={setStemsZip} accept=".zip,application/zip" progress={progress.stems_zip} />
      </div>

      <button onClick={submit} disabled={busy || (nameStatus && !nameStatus.available)} className="w-full py-3 rounded-full font-black text-sm bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-50" data-testid="admin-beats-submit">
        {busy ? (() => {
          const parts = Object.entries(progress);
          if (!parts.length) return "Uploading…";
          const overall = Math.round(parts.reduce((s, [, v]) => s + (v || 0), 0) / parts.length);
          const active = parts.find(([, v]) => v > 0 && v < 100);
          return active ? `Uploading ${active[0].replace("_", " ")}… ${overall}%` : `Uploading… ${overall}%`;
        })() : "Upload beat"}
      </button>
    </div>
  );
}

function FileInput({ label, onChange, accept, testid, progress }) {
  const [name, setName] = useState("");
  return (
    <div>
      <label className="block text-[11px] text-white/50 mb-1">{label}</label>
      <label className="block w-full px-3 py-2 rounded-md bg-black border border-[#262626] text-sm cursor-pointer hover:border-emerald-400/40 truncate relative overflow-hidden" data-testid={testid}>
        <input type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0] || null; setName(f?.name || ""); onChange(f); }} />
        {typeof progress === "number" && progress > 0 ? (
          <div className="absolute inset-y-0 left-0 bg-emerald-500/25 pointer-events-none" style={{ width: `${progress}%` }} />
        ) : null}
        <span className="relative">
          {name || <span className="text-white/40">Choose file…</span>}
          {typeof progress === "number" && progress > 0 ? <span className="ml-2 text-emerald-300 font-bold">{progress}%</span> : null}
        </span>
      </label>
    </div>
  );
}

function BeatRow({ b, token, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [soldExclusive, setSoldExclusive] = useState(!!b.sold_exclusive);
  // v19.30 — Manual purchase capture. Admin collects money out-of-band
  // (DM, PayPal, cash) then records the sale here — server auto-creates
  // the buyer's Crow account, builds the license PDF, and emails
  // download + set-password links (or returns them for manual forward
  // if Resend isn't configured).
  const [rec, setRec] = useState({ email: "", name: "", stage: "", tier: "mp3" });
  const [recResult, setRecResult] = useState(null);

  const save = async () => {
    try {
      const res = await fetch(`${API_URL}/api/store/admin/beats/${b.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sold_exclusive: soldExclusive }),
      });
      if (!res.ok) throw new Error("Save failed");
      toast.success("Saved");
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e.message); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${b.beat_name}" (${b.artist_tag})?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/store/admin/beats/${b.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Deleted");
      onChanged();
    } catch (e) { toast.error(e.message); }
  };

  const recordPurchase = async () => {
    if (!rec.email.trim()) { toast.error("Buyer email required"); return; }
    try {
      const res = await fetch(`${API_URL}/api/store/admin/purchases`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          beat_id: b.id,
          tier: rec.tier,
          buyer_email: rec.email.trim(),
          buyer_name: rec.name.trim(),
          buyer_stage: rec.stage.trim(),
        }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Record failed"); }
      const data = await res.json();
      setRecResult(data);
      toast.success(`Recorded purchase — account ${data.auto_account_created ? "created" : "linked"}${data.email_sent ? " · email sent" : " · copy the links below"}`);
      onChanged();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="rounded-2xl bg-[#0a0a0a] border border-[#1f1f1f] p-4" data-testid={`admin-beat-row-${b.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="font-mono font-bold text-lg">"{b.beat_name}"</div>
          <div className="text-xs text-white/50">{b.artist_tag} · {b.bpm} BPM {b.musical_key ? `· ${b.musical_key}` : ""}</div>
          <div className="mt-1 text-[10px] text-white/40 break-all">/beats/{b.artist_slug}/{b.name_slug}</div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`/beats/${b.artist_slug}/${b.name_slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/5 border border-white/10 hover:border-emerald-400/40" title="View public page"><Eye size={14} weight="bold" /></a>
          <button onClick={() => setRecording((v) => !v)} className="px-3 py-1.5 text-xs rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25" data-testid={`admin-beat-record-${b.id}`}>{recording ? "Close sale" : "Record sale"}</button>
          <button onClick={() => setEditing((v) => !v)} className="px-3 py-1.5 text-xs rounded-full bg-white/5 border border-white/10 hover:border-emerald-400/40" data-testid={`admin-beat-edit-${b.id}`}>{editing ? "Close" : "Edit"}</button>
          <button onClick={del} className="p-2 rounded-full bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-300" data-testid={`admin-beat-delete-${b.id}`}><Trash size={14} weight="bold" /></button>
        </div>
      </div>
      {editing ? (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
          <label className="flex items-center gap-2 text-xs pb-1">
            <input type="checkbox" checked={soldExclusive} onChange={(e) => setSoldExclusive(e.target.checked)} data-testid={`admin-beat-sold-${b.id}`} /> Sold exclusive
          </label>
          <button onClick={save} className="col-span-2 sm:col-span-5 py-2 rounded-full bg-emerald-500 text-black text-sm font-bold" data-testid={`admin-beat-save-${b.id}`}>Save</button>
        </div>
      ) : (
        <div className="mt-2 text-xs text-white/50">
          MP3 {priceFmt(b.prices?.mp3)} · WAV + Stems {priceFmt(b.prices?.wav)} · Exclusive {priceFmt(b.prices?.exclusive)}
          {b.sold_exclusive ? <span className="ml-2 px-2 py-0.5 rounded bg-red-500/20 text-red-300 font-bold">SOLD</span> : null}
        </div>
      )}
      {recording ? (
        <div className="mt-4 rounded-xl bg-emerald-500/5 border border-emerald-400/30 p-3 space-y-3" data-testid={`admin-record-panel-${b.id}`}>
          <div className="text-[11px] text-emerald-200 font-bold uppercase tracking-wide">Record manual sale</div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <input value={rec.email} onChange={(e) => setRec({ ...rec, email: e.target.value })} placeholder="Buyer email" type="email" className="px-2 py-1.5 rounded-md bg-black border border-[#262626] text-xs sm:col-span-2" data-testid={`admin-record-email-${b.id}`} />
            <input value={rec.name} onChange={(e) => setRec({ ...rec, name: e.target.value })} placeholder="Legal name (optional)" className="px-2 py-1.5 rounded-md bg-black border border-[#262626] text-xs" />
            <input value={rec.stage} onChange={(e) => setRec({ ...rec, stage: e.target.value })} placeholder="Stage name (optional)" className="px-2 py-1.5 rounded-md bg-black border border-[#262626] text-xs" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={rec.tier} onChange={(e) => setRec({ ...rec, tier: e.target.value })} className="px-2 py-1.5 rounded-md bg-black border border-[#262626] text-xs" data-testid={`admin-record-tier-${b.id}`}>
              <option value="mp3">MP3 Lease · {priceFmt(b.prices?.mp3)}</option>
              <option value="wav">WAV + Stems Lease · {priceFmt(b.prices?.wav)}</option>
              <option value="exclusive">Exclusive · {priceFmt(b.prices?.exclusive)}</option>
            </select>
            <button onClick={recordPurchase} className="px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-500 text-black" data-testid={`admin-record-submit-${b.id}`}>Record + email buyer</button>
          </div>
          {recResult ? (
            <div className="text-[11px] space-y-1 pt-2 border-t border-white/10">
              <div className="text-emerald-200 font-bold">{recResult.email_sent ? "Email sent to buyer" : "Email NOT sent — forward these links manually:"}</div>
              <div className="break-all"><span className="text-white/40">Downloads:</span> <a href={recResult.success_link} target="_blank" rel="noreferrer" className="underline text-emerald-300">{recResult.success_link}</a></div>
              {recResult.set_password_link ? (
                <div className="break-all"><span className="text-white/40">Set password:</span> <a href={recResult.set_password_link} target="_blank" rel="noreferrer" className="underline text-emerald-300">{recResult.set_password_link}</a></div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
